import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { cmsService, type AdminBookingMetadata } from '@services/api'
import {
  AlertTriangle,
  Calendar,
  CheckCircle,
  Clock,
  CreditCard,
  Eye,
  MapPin,
  Phone,
  Plus,
  RefreshCcw,
  Search,
  User,
  Users,
  XCircle,
} from 'lucide-react'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Center,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  Heading,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberInput,
  NumberInputField,
  Select,
  SimpleGrid,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react'

interface Booking {
  id: string
  bookingReference: string
  passenger?: {
    id: string
    name: string
    phone: string
    email: string
    isGuest?: boolean
  }
  route?: {
    id: string
    name: string
    code: string
    startPoint: string
    endPoint: string
  }
  trip?: {
    id: string
    status: string
    scheduledDeparture?: string | null
    scheduledArrival?: string | null
    actualDeparture?: string | null
    actualArrival?: string | null
    timetable?: {
      dayOfWeek: number
      departureTime?: string | null
      arrivalTime?: string | null
    } | null
  }
  status: string
  totalFare: number
  seatCount: number
  createdAt: string
  updatedAt: string
  payment?: {
    id: string
    amount: number
    status: string
    method?: string
  } | null
  seatNumbers?: string[]
  boardedAt?: string | null
  boardingStop?: {
    id: string
    name: string
    stopCode: string
    landmark?: string | null
    stopOrder?: number | null
    estimatedPickupAt?: string | null
    estimatedPickupApproximate?: boolean
  } | null
  alightingStop?: {
    id: string
    name: string
    stopCode: string
    landmark?: string | null
  } | null
  adminMetadata?: AdminBookingMetadata
}

interface PassengerOption {
  id: string
  firstName: string
  lastName: string
  phoneNumber: string
  email?: string
  walletBalance?: number
}

interface TripOption {
  id: string
  status: string
  scheduledDeparture?: string
  scheduledArrival?: string
  route?: {
    id: string
    name: string
    startPoint: string
    endPoint: string
  }
  daladala?: {
    registrationNumber: string
  }
}

interface RouteLookupOption {
  id: string
  name?: string
  routeName?: string
  startPoint: string
  endPoint: string
  baseFare?: number
  estimatedDurationMinutes?: number
  estimatedTimeMinutes?: number
}

interface JourneyStopOption {
  id: string
  name: string
  stopOrder: number
  distanceFromStart?: number
  estimatedTimeFromStart?: number
}

interface SeatOption {
  seat_number: string
  status: string
  is_available: boolean
}

interface ManualBookingFormData {
  userId: string
  tripId: string
  boardingStopId: string
  alightingStopId: string
  seatNumbers: string
  fareAmount: number
  paymentStatus: 'pending' | 'paid'
  paymentResponsibility: 'passenger' | 'admin' | 'account'
  paymentSourceAccount: string
  settlementStatus: 'pending' | 'cleared'
  invoiceGroup: string
  passengerName: string
  passengerPhone: string
  internalNote: string
}

const TRIP_DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const

function formatStopLabel(stop: NonNullable<Booking['boardingStop']> | NonNullable<Booking['alightingStop']> | undefined | null): string {
  if (!stop) return '—'
  const parts = [stop.name]
  if (stop.landmark) parts.push(`(${stop.landmark})`)
  parts.push(`[${stop.stopCode}]`)
  return parts.join(' ')
}

function formatTripDateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
}

function formatTripTimeOnly(iso: string | null | undefined): string {
  if (!iso) return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
}

function formatTripDateOnly(iso: string | null | undefined): string {
  if (!iso) return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })
}

function formatTripTimeForStop(iso: string | Date | null | undefined): string {
  if (!iso) return '—'
  const date = iso instanceof Date ? iso : new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
}

function formatDateInputValue(date: Date): string {
  const year = date.getFullYear()
  const month = `${date.getMonth() + 1}`.padStart(2, '0')
  const day = `${date.getDate()}`.padStart(2, '0')
  return `${year}-${month}-${day}`
}

function getRouteDisplayName(route: RouteLookupOption | TripOption['route'] | null | undefined): string {
  if (!route) return 'Route'
  return ('name' in route && route.name) || ('routeName' in route && route.routeName) || 'Route'
}

function getStopPassingTime(
  tripDeparture: string | undefined,
  stop: JourneyStopOption,
  route?: RouteLookupOption,
  totalStops?: number
): Date | null {
  if (!tripDeparture) return null

  const departure = new Date(tripDeparture)
  if (Number.isNaN(departure.getTime())) return null

  if (typeof stop.estimatedTimeFromStart === 'number' && stop.estimatedTimeFromStart > 0) {
    return new Date(departure.getTime() + stop.estimatedTimeFromStart * 60 * 1000)
  }

  const routeDuration =
    route?.estimatedDurationMinutes ??
    route?.estimatedTimeMinutes

  if (typeof routeDuration === 'number' && routeDuration > 0 && stop.stopOrder > 1) {
    const maxStopOrder = Math.max(totalStops || stop.stopOrder, 2)
    const fraction = (stop.stopOrder - 1) / Math.max(maxStopOrder - 1, 1)
    return new Date(departure.getTime() + Math.round(routeDuration * fraction) * 60 * 1000)
  }

  return stop.stopOrder > 1 ? departure : departure
}

function buildStopOptionLabel(
  stop: JourneyStopOption,
  tripDeparture: string | undefined,
  route?: RouteLookupOption,
  totalStops?: number
): string {
  const passingTime = getStopPassingTime(tripDeparture, stop, route, totalStops)
  const timeLabel = passingTime ? formatTripTimeForStop(passingTime) : 'Time TBD'
  return `${stop.name} · ${timeLabel}`
}

function parseSeatNumbers(input: string): number[] {
  return Array.from(
    new Set(
      input
        .split(',')
        .map((seat) => Number(seat.trim()))
        .filter((seat) => Number.isInteger(seat) && seat > 0)
    )
  )
}

function isUnusedTicket(booking: Booking) {
  return (booking.status === 'confirmed' || booking.status === 'pending') && !booking.boardedAt
}

function getStatusColor(status: string) {
  switch (status) {
    case 'confirmed':
    case 'completed':
      return 'green'
    case 'pending':
      return 'orange'
    case 'cancelled':
      return 'red'
    default:
      return 'gray'
  }
}

function getPaymentStatusColor(status: string) {
  switch (status) {
    case 'paid':
    case 'cleared':
      return 'green'
    case 'pending':
      return 'orange'
    case 'refunded':
      return 'purple'
    case 'failed':
      return 'red'
    default:
      return 'gray'
  }
}

function getResponsibilityColor(value: string) {
  switch (value) {
    case 'admin':
      return 'blue'
    case 'account':
      return 'purple'
    default:
      return 'gray'
  }
}

function buildPassengerLabel(passenger: PassengerOption): string {
  const fullName = `${passenger.firstName || ''} ${passenger.lastName || ''}`.trim() || 'Unnamed passenger'
  const phone = passenger.phoneNumber || 'No phone'
  const email = passenger.email || 'No email'
  return `${fullName} · ${phone} · ${email}`
}

function sortSeats(seats: SeatOption[]): SeatOption[] {
  return [...seats].sort((left, right) => {
    const leftNumber = Number(left.seat_number)
    const rightNumber = Number(right.seat_number)

    if (!Number.isNaN(leftNumber) && !Number.isNaN(rightNumber)) {
      return leftNumber - rightNumber
    }

    return left.seat_number.localeCompare(right.seat_number)
  })
}

function ManualBookingModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [formData, setFormData] = useState<ManualBookingFormData>({
    userId: '',
    tripId: '',
    boardingStopId: '',
    alightingStopId: '',
    seatNumbers: '',
    fareAmount: 0,
    paymentStatus: 'pending',
    paymentResponsibility: 'passenger',
    paymentSourceAccount: '',
    settlementStatus: 'pending',
    invoiceGroup: '',
    passengerName: '',
    passengerPhone: '',
    internalNote: '',
  })
  const [passengerSearch, setPassengerSearch] = useState('')
  const [selectedPassengerOption, setSelectedPassengerOption] = useState<PassengerOption | null>(null)
  const [selectedSeats, setSelectedSeats] = useState<string[]>([])
  const [selectedRouteId, setSelectedRouteId] = useState('')
  const [selectedTripDate, setSelectedTripDate] = useState(formatDateInputValue(new Date()))
  const [boardingStopSearch, setBoardingStopSearch] = useState('')
  const [alightingStopSearch, setAlightingStopSearch] = useState('')

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedPassengerSearch(passengerSearch.trim())
    }, 250)

    return () => window.clearTimeout(timeoutId)
  }, [passengerSearch])

  const [debouncedPassengerSearch, setDebouncedPassengerSearch] = useState('')

  const { data: passengersData, isFetching: passengersLoading } = useQuery(
    ['booking-passengers-lookup', debouncedPassengerSearch],
    () =>
      cmsService.getPassengers({
        limit: 20,
        search: debouncedPassengerSearch,
      }),
    {
      enabled: isOpen && debouncedPassengerSearch.length >= 2,
      retry: false,
      keepPreviousData: true,
    }
  )

  const { data: routesData, isFetching: routesLoading } = useQuery(
    ['booking-routes-lookup'],
    () => cmsService.getRoutes({ limit: 100 }),
    { enabled: isOpen, retry: false }
  )

  const { data: tripsData } = useQuery(
    ['booking-trips-lookup', selectedRouteId, selectedTripDate],
    () =>
      cmsService.getTrips({
        limit: 100,
        routeId: selectedRouteId || undefined,
        date: selectedTripDate || undefined,
      }),
    { enabled: isOpen, retry: false, keepPreviousData: true }
  )

  const selectedTripRouteIdFromQuery =
    ((tripsData?.data || []) as TripOption[]).find((trip) => trip.id === formData.tripId)?.route?.id || ''
  const activeRouteId = selectedRouteId || selectedTripRouteIdFromQuery

  const { data: routeStopsData } = useQuery(
    ['booking-route-stops', activeRouteId],
    () => cmsService.getRouteStops(activeRouteId),
    { enabled: isOpen && !!activeRouteId, retry: false, keepPreviousData: true }
  )

  const { data: segmentFareData } = useQuery(
    ['booking-segment-fare', activeRouteId, formData.boardingStopId, formData.alightingStopId],
    () =>
      cmsService.getRouteFareBetweenStops(activeRouteId, {
        startStopId: formData.boardingStopId,
        endStopId: formData.alightingStopId,
      }),
    {
      enabled: isOpen && !!activeRouteId && !!formData.boardingStopId && !!formData.alightingStopId,
      retry: false,
      keepPreviousData: true,
    }
  )

  const { data: seatMapData, isFetching: seatMapLoading, error: seatMapError } = useQuery(
    ['booking-seat-map', formData.tripId, formData.boardingStopId, formData.alightingStopId],
    () =>
      cmsService.getTripSeatMap(formData.tripId, {
        fromStopId: formData.boardingStopId || undefined,
        toStopId: formData.alightingStopId || undefined,
      }),
    {
      enabled: isOpen && !!formData.tripId,
      retry: false,
      keepPreviousData: true,
    }
  )

  const createBookingMutation = useMutation((payload: Parameters<typeof cmsService.createBooking>[0]) =>
    cmsService.createBooking(payload)
  )

  useEffect(() => {
    if (!isOpen) return
    setFormData({
      userId: '',
      tripId: '',
      boardingStopId: '',
      alightingStopId: '',
      seatNumbers: '',
      fareAmount: 0,
      paymentStatus: 'pending',
      paymentResponsibility: 'passenger',
      paymentSourceAccount: '',
      settlementStatus: 'pending',
      invoiceGroup: '',
      passengerName: '',
      passengerPhone: '',
      internalNote: '',
    })
    setPassengerSearch('')
    setDebouncedPassengerSearch('')
    setSelectedPassengerOption(null)
    setSelectedSeats([])
    setSelectedRouteId('')
    setSelectedTripDate(formatDateInputValue(new Date()))
    setBoardingStopSearch('')
    setAlightingStopSearch('')
  }, [isOpen])

  const passengers: PassengerOption[] = passengersData?.data || []
  const routes: RouteLookupOption[] = Array.isArray(routesData?.data) ? (routesData.data as unknown as RouteLookupOption[]) : []
  const trips: TripOption[] = (Array.isArray(tripsData?.data) ? tripsData.data : [] as TripOption[])
    .filter((trip) => ['scheduled', 'boarding', 'in_transit'].includes(trip.status))
    .sort((left, right) => {
      const leftTime = left.scheduledDeparture ? new Date(left.scheduledDeparture).getTime() : Number.MAX_SAFE_INTEGER
      const rightTime = right.scheduledDeparture ? new Date(right.scheduledDeparture).getTime() : Number.MAX_SAFE_INTEGER
      return leftTime - rightTime
    })

  const selectedPassenger = selectedPassengerOption
  const selectedTrip = trips.find((trip) => trip.id === formData.tripId)
  const selectedRoute = routes.find((route) => route.id === activeRouteId)
  const routeStopsSource = Array.isArray(routeStopsData?.data) ? routeStopsData.data : []
  const routeStops: JourneyStopOption[] = (routeStopsSource as unknown as JourneyStopOption[]).sort((left, right) => left.stopOrder - right.stopOrder)
  const availableSeats: SeatOption[] = sortSeats(seatMapData?.data?.seats || [])
  const seatMapErrorMessage =
    ((seatMapError as any)?.response?.data?.message as string | undefined) ||
    ((seatMapError as any)?.message as string | undefined) ||
    ''
  const showPassengerSuggestions =
    debouncedPassengerSearch.length >= 2 && formData.userId === '' && passengers.length > 0
  const showPassengerEmptyState =
    debouncedPassengerSearch.length >= 2 &&
    !passengersLoading &&
    passengers.length === 0 &&
    !selectedPassenger

  useEffect(() => {
    setFormData((current) => ({ ...current, seatNumbers: selectedSeats.join(', ') }))
  }, [selectedSeats])

  useEffect(() => {
    setSelectedSeats([])
    setFormData((current) => ({ ...current, seatNumbers: '' }))
  }, [formData.tripId])

  useEffect(() => {
    setFormData((current) => ({ ...current, tripId: '', boardingStopId: '', alightingStopId: '' }))
    setSelectedSeats([])
    setBoardingStopSearch('')
    setAlightingStopSearch('')
  }, [selectedRouteId, selectedTripDate])

  useEffect(() => {
    if (routeStops.length === 0) return

    setFormData((current) => {
      const hasBoarding = routeStops.some((stop) => stop.id === current.boardingStopId)
      const hasAlighting = routeStops.some((stop) => stop.id === current.alightingStopId)
      const firstStopId = routeStops[0]?.id || ''
      const lastStopId = routeStops[routeStops.length - 1]?.id || ''

      return {
        ...current,
        boardingStopId: hasBoarding ? current.boardingStopId : firstStopId,
        alightingStopId: hasAlighting ? current.alightingStopId : lastStopId,
      }
    })
  }, [routeStops])

  useEffect(() => {
    if (!formData.boardingStopId || routeStops.length === 0) return

    const boardingStop = routeStops.find((stop) => stop.id === formData.boardingStopId)
    const alightingStop = routeStops.find((stop) => stop.id === formData.alightingStopId)

    if (!boardingStop) return

    if (!alightingStop || alightingStop.stopOrder <= boardingStop.stopOrder) {
      const nextPossibleStop = routeStops.find((stop) => stop.stopOrder > boardingStop.stopOrder)
      setFormData((current) => ({
        ...current,
        alightingStopId: nextPossibleStop?.id || current.alightingStopId,
      }))
    }
  }, [formData.boardingStopId, formData.alightingStopId, routeStops])

  useEffect(() => {
    const nextFare =
      Number(segmentFareData?.data?.fare ?? segmentFareData?.data?.amount ?? selectedRoute?.baseFare ?? 0)

    setFormData((current) => ({
      ...current,
      fareAmount: nextFare,
    }))
  }, [segmentFareData?.data?.fare, segmentFareData?.data?.amount, selectedRoute?.baseFare])

  useEffect(() => {
    if (!selectedPassenger) return

    setFormData((current) => ({
      ...current,
      passengerName: current.passengerName || `${selectedPassenger.firstName || ''} ${selectedPassenger.lastName || ''}`.trim(),
      passengerPhone: current.passengerPhone || selectedPassenger.phoneNumber || '',
    }))
  }, [selectedPassenger])

  const handleSubmit = async () => {
    const seatNumbers = selectedSeats.length > 0 ? selectedSeats.map((seat) => Number(seat)) : parseSeatNumbers(formData.seatNumbers)

    if (!formData.userId || !formData.tripId || seatNumbers.length === 0 || formData.fareAmount <= 0) {
      toast({
        title: 'Missing information',
        description: 'Choose a commuter, trip, seat numbers, and fare amount before saving.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      })
      return
    }

    try {
      await createBookingMutation.mutateAsync({
        userId: formData.userId,
        tripId: formData.tripId,
        seatNumbers,
        boardingStopId: formData.boardingStopId || undefined,
        alightingStopId: formData.alightingStopId || undefined,
        fareAmount: formData.fareAmount,
        paymentStatus: formData.paymentStatus,
        paymentResponsibility: formData.paymentResponsibility,
        paymentSourceAccount: formData.paymentResponsibility === 'account' ? formData.paymentSourceAccount : '',
        settlementStatus: formData.settlementStatus,
        invoiceGroup: formData.invoiceGroup.trim() || undefined,
        passengerName: formData.passengerName.trim() || undefined,
        passengerPhone: formData.passengerPhone.trim() || undefined,
        internalNote: formData.internalNote.trim() || undefined,
      })

      await queryClient.invalidateQueries('bookings')
      toast({
        title: 'Booking created',
        description: 'The commuter will now find this trip in their bookings.',
        status: 'success',
        duration: 4000,
        isClosable: true,
      })
      onClose()
    } catch (error: any) {
      toast({
        title: 'Could not create booking',
        description: error?.response?.data?.error?.message || 'Please review the booking details and try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    }
  }

  const handlePassengerSelect = (passenger: PassengerOption) => {
    setSelectedPassengerOption(passenger)
    setPassengerSearch(buildPassengerLabel(passenger))
    setDebouncedPassengerSearch('')
    setFormData((current) => ({
      ...current,
      userId: passenger.id,
      passengerName: `${passenger.firstName || ''} ${passenger.lastName || ''}`.trim(),
      passengerPhone: passenger.phoneNumber || '',
    }))
  }

  const toggleSeat = (seat: SeatOption) => {
    if (!seat.is_available) return

    setSelectedSeats((current) =>
      current.includes(seat.seat_number)
        ? current.filter((value) => value !== seat.seat_number)
        : [...current, seat.seat_number]
    )
  }

  const boardingStop = routeStops.find((stop) => stop.id === formData.boardingStopId)
  const alightingStop = routeStops.find((stop) => stop.id === formData.alightingStopId)
  const alightingOptions = routeStops.filter((stop) => !boardingStop || stop.stopOrder > boardingStop.stopOrder)
  const normalizedBoardingSearch = boardingStopSearch.trim().toLowerCase()
  const normalizedAlightingSearch = alightingStopSearch.trim().toLowerCase()
  const filteredBoardingStops = normalizedBoardingSearch.length === 0
    ? routeStops
    : routeStops.filter((stop) =>
        buildStopOptionLabel(stop, selectedTrip?.scheduledDeparture, selectedRoute, routeStops.length)
          .toLowerCase()
          .includes(normalizedBoardingSearch)
      )
  const filteredAlightingStops = normalizedAlightingSearch.length === 0
    ? alightingOptions
    : alightingOptions.filter((stop) =>
        buildStopOptionLabel(stop, selectedTrip?.scheduledDeparture, selectedRoute, routeStops.length)
          .toLowerCase()
          .includes(normalizedAlightingSearch)
      )
  const showBoardingSuggestions =
    activeRouteId !== '' &&
    routeStops.length > 0 &&
    (formData.boardingStopId === '' || normalizedBoardingSearch.length > 0)
  const showAlightingSuggestions =
    activeRouteId !== '' &&
    alightingOptions.length > 0 &&
    (formData.alightingStopId === '' || normalizedAlightingSearch.length > 0)

  useEffect(() => {
    if (!boardingStop) return

    setBoardingStopSearch(buildStopOptionLabel(boardingStop, selectedTrip?.scheduledDeparture, selectedRoute, routeStops.length))
  }, [boardingStop, selectedTrip?.scheduledDeparture, selectedRoute, routeStops.length])

  useEffect(() => {
    if (!alightingStop) return

    setAlightingStopSearch(buildStopOptionLabel(alightingStop, selectedTrip?.scheduledDeparture, selectedRoute, routeStops.length))
  }, [alightingStop, selectedTrip?.scheduledDeparture, selectedRoute, routeStops.length])

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay backdropFilter="blur(6px)" />
      <ModalContent borderRadius="2xl">
        <ModalHeader>Create Admin Booking</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={5} align="stretch">
            <FormControl isRequired>
              <FormLabel>Find Commuter</FormLabel>
              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <Search size={16} color="var(--chakra-colors-gray-400)" />
                </InputLeftElement>
                <Input
                  placeholder="Type name, phone number, or email"
                  value={passengerSearch}
                  onChange={(e) => {
                    setPassengerSearch(e.target.value)
                    setSelectedPassengerOption(null)
                    setFormData((current) => ({ ...current, userId: '' }))
                  }}
                />
              </InputGroup>
              <Text fontSize="xs" color="gray.500" mt={2}>
                Search by phone, email, or passenger name. Results appear as you type.
              </Text>
              {passengersLoading ? (
                <HStack mt={3} spacing={2}>
                  <Spinner size="sm" />
                  <Text fontSize="sm" color="gray.500">Searching commuters…</Text>
                </HStack>
              ) : null}
              {showPassengerSuggestions ? (
                <Card mt={3} border="1px solid" borderColor="gray.200" shadow="none">
                  <CardBody py={2}>
                    <VStack spacing={1} align="stretch">
                      {passengers.map((passenger) => (
                        <Button
                          key={passenger.id}
                          variant="ghost"
                          justifyContent="start"
                          h="auto"
                          py={3}
                          onClick={() => handlePassengerSelect(passenger)}
                        >
                          <VStack align="start" spacing={0}>
                            <Text fontWeight="semibold">
                              {`${passenger.firstName || ''} ${passenger.lastName || ''}`.trim() || 'Unnamed passenger'}
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              {passenger.phoneNumber || 'No phone'} · {passenger.email || 'No email'}
                            </Text>
                          </VStack>
                        </Button>
                      ))}
                    </VStack>
                  </CardBody>
                </Card>
              ) : null}
              {showPassengerEmptyState ? (
                <Text fontSize="sm" color="gray.500" mt={3}>No commuter found for that search.</Text>
              ) : null}
              {selectedPassenger ? (
                <Card mt={3} border="1px solid" borderColor="green.200" bg="green.50" shadow="none">
                  <CardBody py={3}>
                    <VStack align="start" spacing={1}>
                      <Text fontSize="sm" fontWeight="semibold" color="green.700">
                        Selected commuter
                      </Text>
                      <Text fontSize="sm">
                        {`${selectedPassenger.firstName || ''} ${selectedPassenger.lastName || ''}`.trim() || 'Unnamed passenger'}
                      </Text>
                      <Text fontSize="xs" color="gray.600">
                        {selectedPassenger.phoneNumber || 'No phone'} · {selectedPassenger.email || 'No email'}
                      </Text>
                      <Text fontSize="xs" color="gray.600">
                        Wallet balance: TZS {Number(selectedPassenger.walletBalance ?? 0).toLocaleString()}
                      </Text>
                    </VStack>
                  </CardBody>
                </Card>
              ) : null}
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Route And Date</FormLabel>
              <Grid templateColumns={{ base: '1fr', md: '1.4fr 1fr' }} gap={4}>
                <FormControl>
                  <Select
                    placeholder={routesLoading ? 'Loading routes…' : 'All routes'}
                    value={selectedRouteId}
                    onChange={(e) => setSelectedRouteId(e.target.value)}
                  >
                    {routes.map((route) => (
                      <option key={route.id} value={route.id}>
                        {getRouteDisplayName(route)} · {route.startPoint} to {route.endPoint}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl>
                  <Input
                    type="date"
                    value={selectedTripDate}
                    onChange={(e) => setSelectedTripDate(e.target.value)}
                  />
                </FormControl>
              </Grid>
              <Text fontSize="xs" color="gray.500" mt={2}>
                Narrow the journey first, then choose the exact departure time and seats.
              </Text>
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Trip Time</FormLabel>
              <Card border="1px solid" borderColor="gray.200" shadow="none">
                <CardBody>
                  {trips.length > 0 ? (
                    <VStack align="stretch" spacing={3} maxH="260px" overflowY="auto">
                      {trips.map((trip) => {
                        const isSelected = trip.id === formData.tripId
                        return (
                          <Button
                            key={trip.id}
                            variant={isSelected ? 'solid' : 'outline'}
                            colorScheme={isSelected ? 'brand' : 'gray'}
                            justifyContent="space-between"
                            h="auto"
                            py={4}
                            px={4}
                            onClick={() => {
                              if (!selectedRouteId && trip.route?.id) {
                                setSelectedRouteId(trip.route.id)
                              }
                              setFormData((current) => ({ ...current, tripId: trip.id }))
                            }}
                          >
                            <VStack align="start" spacing={1} flex="1" textAlign="left">
                              <Text fontWeight="bold">
                                {formatTripTimeOnly(trip.scheduledDeparture)} to {formatTripTimeOnly(trip.scheduledArrival)}
                              </Text>
                              <Text fontSize="sm" opacity={0.9}>
                                {trip.route?.startPoint} to {trip.route?.endPoint}
                              </Text>
                              <Text fontSize="xs" opacity={0.8}>
                                {getRouteDisplayName(trip.route)} · {formatTripDateOnly(trip.scheduledDeparture)} · {trip.daladala?.registrationNumber || 'Vehicle'}
                              </Text>
                            </VStack>
                            <Badge colorScheme={trip.status === 'scheduled' ? 'blue' : trip.status === 'boarding' ? 'orange' : 'green'}>
                              {trip.status.replace('_', ' ')}
                            </Badge>
                          </Button>
                        )
                      })}
                    </VStack>
                  ) : (
                    <Text fontSize="sm" color="gray.500">
                      No trips match this route/date yet. Try another route or date.
                    </Text>
                  )}
                </CardBody>
              </Card>
              <Text fontSize="xs" color="gray.500" mt={2}>
                Choose the trip time first. Seat availability updates from the live trip seat map after selection.
              </Text>
            </FormControl>

            <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4}>
              <FormControl isRequired>
                <FormLabel>Boarding Stop</FormLabel>
                <Input
                  placeholder={activeRouteId ? 'Type stop name or time' : 'Choose route first'}
                  value={boardingStopSearch}
                  onChange={(e) => {
                    setBoardingStopSearch(e.target.value)
                    setFormData((current) => ({
                      ...current,
                      boardingStopId: '',
                    }))
                  }}
                  isDisabled={!activeRouteId || routeStops.length === 0}
                />
                {showBoardingSuggestions ? (
                  <Card mt={3} border="1px solid" borderColor="gray.200" shadow="none">
                    <CardBody py={2}>
                      <VStack spacing={1} align="stretch" maxH="220px" overflowY="auto">
                        {filteredBoardingStops.length > 0 ? (
                          filteredBoardingStops.map((stop) => (
                            <Button
                              key={stop.id}
                              variant={formData.boardingStopId === stop.id ? 'solid' : 'ghost'}
                              colorScheme={formData.boardingStopId === stop.id ? 'brand' : 'gray'}
                              justifyContent="start"
                              h="auto"
                              py={3}
                              onClick={() => {
                                setFormData((current) => ({
                                  ...current,
                                  boardingStopId: stop.id,
                                }))
                                setBoardingStopSearch(
                                  buildStopOptionLabel(stop, selectedTrip?.scheduledDeparture, selectedRoute, routeStops.length)
                                )
                              }}
                            >
                              <VStack align="start" spacing={0}>
                                <Text fontWeight="semibold">{stop.name}</Text>
                                <Text fontSize="xs" color="gray.500">
                                  Pickup {formatTripTimeForStop(getStopPassingTime(selectedTrip?.scheduledDeparture, stop, selectedRoute, routeStops.length))}
                                </Text>
                              </VStack>
                            </Button>
                          ))
                        ) : (
                          <Text fontSize="sm" color="gray.500" px={2} py={2}>
                            No boarding stop matches that search.
                          </Text>
                        )}
                      </VStack>
                    </CardBody>
                  </Card>
                ) : null}
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Dropoff Stop</FormLabel>
                <Input
                  placeholder={activeRouteId ? 'Type stop name or time' : 'Choose route first'}
                  value={alightingStopSearch}
                  onChange={(e) => {
                    setAlightingStopSearch(e.target.value)
                    setFormData((current) => ({
                      ...current,
                      alightingStopId: '',
                    }))
                  }}
                  isDisabled={!activeRouteId || alightingOptions.length === 0}
                />
                {showAlightingSuggestions ? (
                  <Card mt={3} border="1px solid" borderColor="gray.200" shadow="none">
                    <CardBody py={2}>
                      <VStack spacing={1} align="stretch" maxH="220px" overflowY="auto">
                        {filteredAlightingStops.length > 0 ? (
                          filteredAlightingStops.map((stop) => (
                            <Button
                              key={stop.id}
                              variant={formData.alightingStopId === stop.id ? 'solid' : 'ghost'}
                              colorScheme={formData.alightingStopId === stop.id ? 'brand' : 'gray'}
                              justifyContent="start"
                              h="auto"
                              py={3}
                              onClick={() => {
                                setFormData((current) => ({
                                  ...current,
                                  alightingStopId: stop.id,
                                }))
                                setAlightingStopSearch(
                                  buildStopOptionLabel(stop, selectedTrip?.scheduledDeparture, selectedRoute, routeStops.length)
                                )
                              }}
                            >
                              <VStack align="start" spacing={0}>
                                <Text fontWeight="semibold">{stop.name}</Text>
                                <Text fontSize="xs" color="gray.500">
                                  Around {formatTripTimeForStop(getStopPassingTime(selectedTrip?.scheduledDeparture, stop, selectedRoute, routeStops.length))}
                                </Text>
                              </VStack>
                            </Button>
                          ))
                        ) : (
                          <Text fontSize="sm" color="gray.500" px={2} py={2}>
                            No dropoff stop matches that search.
                          </Text>
                        )}
                      </VStack>
                    </CardBody>
                  </Card>
                ) : null}
              </FormControl>
            </Grid>

            <Text fontSize="xs" color="gray.500" mt={-2}>
              Fare and seat availability now follow the selected boarding and dropoff stops.
            </Text>
            {selectedTrip && boardingStop ? (
              <Text fontSize="xs" color="gray.600" mt={-2}>
                Pickup at {boardingStop.name}: {formatTripTimeForStop(getStopPassingTime(selectedTrip.scheduledDeparture, boardingStop, selectedRoute, routeStops.length))}
                {alightingStop ? ` · Dropoff ${alightingStop.name}` : ''}
              </Text>
            ) : null}

            <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4}>
              <FormControl isRequired>
                <FormLabel>Fare Amount (TZS)</FormLabel>
                <NumberInput
                  min={0}
                  value={formData.fareAmount}
                  onChange={(_, valueAsNumber) =>
                    setFormData((current) => ({ ...current, fareAmount: Number.isNaN(valueAsNumber) ? 0 : valueAsNumber }))
                  }
                >
                  <NumberInputField />
                </NumberInput>
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Selected Seats</FormLabel>
                <Input
                  placeholder="Choose from seat layout below"
                  value={formData.seatNumbers}
                  onChange={(e) => {
                    setFormData((current) => ({ ...current, seatNumbers: e.target.value }))
                    setSelectedSeats(
                      e.target.value
                        .split(',')
                        .map((seat) => seat.trim())
                        .filter(Boolean)
                    )
                  }}
                />
              </FormControl>
            </Grid>

            <Card bg="gray.50" border="1px solid" borderColor="gray.200">
              <CardBody>
                <VStack align="stretch" spacing={3}>
                  <HStack justify="space-between">
                    <VStack align="start" spacing={0}>
                      <Text fontWeight="semibold">Seat Layout</Text>
                      <Text fontSize="xs" color="gray.500">
                        {selectedTrip
                          ? `${selectedTrip.route?.name || 'Trip'} · ${formatTripDateOnly(selectedTrip.scheduledDeparture)} · ${formatTripTimeOnly(selectedTrip.scheduledDeparture)} · ${selectedTrip.daladala?.registrationNumber || 'Vehicle'}`
                          : 'Choose a trip to load available seats'}
                      </Text>
                    </VStack>
                    {seatMapLoading ? <Spinner size="sm" /> : null}
                  </HStack>

                  {formData.tripId && availableSeats.length > 0 ? (
                    <>
                      <SimpleGrid columns={{ base: 4, md: 6 }} spacing={2}>
                        {availableSeats.map((seat) => {
                          const selected = selectedSeats.includes(seat.seat_number)
                          const isAvailable = seat.is_available

                          return (
                            <Button
                              key={seat.seat_number}
                              size="sm"
                              variant={selected ? 'solid' : 'outline'}
                              colorScheme={selected ? 'brand' : isAvailable ? 'gray' : 'red'}
                              isDisabled={!isAvailable}
                              onClick={() => toggleSeat(seat)}
                            >
                              {seat.seat_number}
                            </Button>
                          )
                        })}
                      </SimpleGrid>
                      <Text fontSize="xs" color="gray.500">
                        Gray seats are available. Selected seats turn green. Unavailable seats are disabled.
                      </Text>
                    </>
                  ) : formData.tripId ? (
                    seatMapErrorMessage ? (
                      <Alert status="warning" borderRadius="lg">
                        <AlertIcon />
                        <AlertDescription fontSize="sm">
                          {seatMapErrorMessage}
                        </AlertDescription>
                      </Alert>
                    ) : (
                      <Text fontSize="sm" color="gray.500">
                        No seat layout returned right now. You can still type seat numbers manually if needed.
                      </Text>
                    )
                  ) : (
                    <Text fontSize="sm" color="gray.500">
                      Pick a trip first, then the live available seats will load here.
                    </Text>
                  )}
                </VStack>
              </CardBody>
            </Card>

            <Grid templateColumns={{ base: '1fr', md: '1fr 1fr 1fr' }} gap={4}>
              <FormControl>
                <FormLabel>Payment Status</FormLabel>
                <Select
                  value={formData.paymentStatus}
                  onChange={(e) =>
                    setFormData((current) => ({ ...current, paymentStatus: e.target.value as ManualBookingFormData['paymentStatus'] }))
                  }
                >
                  <option value="pending">Pending</option>
                  <option value="paid">Paid</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Who Pays?</FormLabel>
                <Select
                  value={formData.paymentResponsibility}
                  onChange={(e) =>
                    setFormData((current) => ({
                      ...current,
                      paymentResponsibility: e.target.value as ManualBookingFormData['paymentResponsibility'],
                      paymentSourceAccount: e.target.value === 'account' ? current.paymentSourceAccount : '',
                    }))
                  }
                >
                  <option value="passenger">Passenger</option>
                  <option value="admin">Admin</option>
                  <option value="account">Account</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Settlement</FormLabel>
                <Select
                  value={formData.settlementStatus}
                  onChange={(e) =>
                    setFormData((current) => ({ ...current, settlementStatus: e.target.value as ManualBookingFormData['settlementStatus'] }))
                  }
                >
                  <option value="pending">Pending</option>
                  <option value="cleared">Cleared</option>
                </Select>
              </FormControl>
            </Grid>

            {formData.paymentResponsibility === 'account' ? (
              <FormControl isRequired>
                <FormLabel>Account Name</FormLabel>
                <Input
                  placeholder="Example: School Account / Corporate Account"
                  value={formData.paymentSourceAccount}
                  onChange={(e) => setFormData((current) => ({ ...current, paymentSourceAccount: e.target.value }))}
                />
              </FormControl>
            ) : null}

            <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4}>
              <FormControl>
                <FormLabel>Traveller Name For Ticket</FormLabel>
                <Input
                  placeholder="Prefilled from commuter if present, editable by admin"
                  value={formData.passengerName}
                  onChange={(e) => setFormData((current) => ({ ...current, passengerName: e.target.value }))}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Traveller Phone For Ticket</FormLabel>
                <Input
                  placeholder="Prefilled if present, editable by admin"
                  value={formData.passengerPhone}
                  onChange={(e) => setFormData((current) => ({ ...current, passengerPhone: e.target.value }))}
                />
              </FormControl>
            </Grid>

            <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4}>
              <FormControl>
                <FormLabel>Invoice Group</FormLabel>
                <Input
                  placeholder="Optional end-of-day invoice tag"
                  value={formData.invoiceGroup}
                  onChange={(e) => setFormData((current) => ({ ...current, invoiceGroup: e.target.value }))}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Internal Note</FormLabel>
                <Input
                  placeholder="Optional admin note"
                  value={formData.internalNote}
                  onChange={(e) => setFormData((current) => ({ ...current, internalNote: e.target.value }))}
                />
              </FormControl>
            </Grid>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <HStack spacing={3}>
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button colorScheme="brand" onClick={handleSubmit} isLoading={createBookingMutation.isLoading}>
              Save Booking
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default function BookingsPage() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const manualBookingModal = useDisclosure()
  const detailModal = useDisclosure()
  const refundModal = useDisclosure()

  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null)
  const [refundReason, setRefundReason] = useState('Ticket voided by admin — unused booking')
  const [refundAmount, setRefundAmount] = useState(0)

  const [searchTerm, setSearchTerm] = useState('')
  const [selectedStatus, setSelectedStatus] = useState('all')
  const [selectedPaymentStatus, setSelectedPaymentStatus] = useState('all')
  const [selectedSettlementStatus, setSelectedSettlementStatus] = useState('all')
  const [selectedResponsibility, setSelectedResponsibility] = useState('all')
  const [accountFilter, setAccountFilter] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 10

  const { data: bookingsData, isLoading: bookingsLoading } = useQuery(
    'bookings',
    () => cmsService.getBookings({ limit: 100 }),
    { retry: false }
  )

  const processRefundMutation = useMutation(
    ({ bookingId, payload }: { bookingId: string; payload: { reason?: string; refundAmount?: number } }) =>
      cmsService.processRefund(bookingId, payload)
  )

  const updateBookingMutation = useMutation(
    ({ bookingId, payload }: { bookingId: string; payload: Parameters<typeof cmsService.updateBooking>[1] }) =>
      cmsService.updateBooking(bookingId, payload)
  )

  const bookings: Booking[] = bookingsData?.data || []

  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, selectedStatus, selectedPaymentStatus, selectedSettlementStatus, selectedResponsibility, accountFilter])

  const filteredBookings = useMemo(() => {
    return bookings.filter((booking) => {
      const passengerName = booking.passenger?.name?.toLowerCase() || ''
      const passengerPhone = booking.passenger?.phone?.toLowerCase() || ''
      const routeName = booking.route?.name?.toLowerCase() || ''
      const bookingNumber = booking.bookingReference?.toLowerCase() || ''
      const accountName = booking.adminMetadata?.paymentSourceAccount?.toLowerCase() || ''
      const search = searchTerm.trim().toLowerCase()

      const matchesSearch =
        !search ||
        bookingNumber.includes(search) ||
        passengerName.includes(search) ||
        passengerPhone.includes(search) ||
        routeName.includes(search) ||
        accountName.includes(search)

      const matchesStatus =
        selectedStatus === 'all'
          ? true
          : selectedStatus === 'unused'
            ? isUnusedTicket(booking)
            : booking.status === selectedStatus

      const paymentStatus = booking.payment?.status || 'pending'
      const matchesPaymentStatus = selectedPaymentStatus === 'all' || paymentStatus === selectedPaymentStatus

      const settlementStatus = booking.adminMetadata?.settlementStatus || 'pending'
      const matchesSettlement = selectedSettlementStatus === 'all' || settlementStatus === selectedSettlementStatus

      const responsibility = booking.adminMetadata?.paymentResponsibility || 'passenger'
      const matchesResponsibility = selectedResponsibility === 'all' || responsibility === selectedResponsibility

      const matchesAccount =
        !accountFilter.trim() ||
        accountName.includes(accountFilter.trim().toLowerCase())

      return matchesSearch && matchesStatus && matchesPaymentStatus && matchesSettlement && matchesResponsibility && matchesAccount
    })
  }, [
    accountFilter,
    bookings,
    searchTerm,
    selectedPaymentStatus,
    selectedResponsibility,
    selectedSettlementStatus,
    selectedStatus,
  ])

  const paginatedBookings = filteredBookings.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
  const totalPages = Math.max(1, Math.ceil(filteredBookings.length / itemsPerPage))

  const invoiceBookings = filteredBookings.filter((booking) => booking.adminMetadata?.bookingSource === 'admin')
  const invoiceTotal = invoiceBookings.reduce((sum, booking) => sum + Number(booking.totalFare || 0), 0)
  const clearedTotal = invoiceBookings
    .filter((booking) => booking.adminMetadata?.settlementStatus === 'cleared')
    .reduce((sum, booking) => sum + Number(booking.totalFare || 0), 0)
  const outstandingTotal = invoiceBookings
    .filter((booking) => (booking.adminMetadata?.settlementStatus || 'pending') !== 'cleared')
    .reduce((sum, booking) => sum + Number(booking.totalFare || 0), 0)

  const stats = [
    { title: 'Total Bookings', value: bookings.length, color: 'blue', icon: Calendar },
    { title: 'Admin Booked', value: bookings.filter((booking) => booking.adminMetadata?.bookingSource === 'admin').length, color: 'purple', icon: User },
    { title: 'Unused Tickets', value: bookings.filter(isUnusedTicket).length, color: 'orange', icon: AlertTriangle },
    {
      title: 'Pending Settlement',
      value: bookings.filter(
        (booking) =>
          booking.adminMetadata?.bookingSource === 'admin' &&
          (booking.adminMetadata?.settlementStatus || 'pending') === 'pending'
      ).length,
      color: 'yellow',
      icon: Clock,
    },
    {
      title: 'Cleared',
      value: bookings.filter(
        (booking) =>
          booking.adminMetadata?.bookingSource === 'admin' &&
          booking.adminMetadata?.settlementStatus === 'cleared'
      ).length,
      color: 'green',
      icon: CheckCircle,
    },
    { title: 'Refunded', value: bookings.filter((booking) => booking.payment?.status === 'refunded').length, color: 'pink', icon: RefreshCcw },
  ]

  const handleViewDetails = (booking: Booking) => {
    setSelectedBooking(booking)
    detailModal.onOpen()
  }

  const handleOpenRefund = (booking: Booking) => {
    setSelectedBooking(booking)
    setRefundAmount(booking.totalFare ?? booking.payment?.amount ?? 0)
    setRefundReason('Ticket voided by admin — unused booking')
    refundModal.onOpen()
  }

  const handleConfirmRefund = async () => {
    if (!selectedBooking) return

    try {
      await processRefundMutation.mutateAsync({
        bookingId: selectedBooking.id,
        payload: { reason: refundReason || 'Voided by admin', refundAmount: refundAmount || undefined },
      })
      await queryClient.invalidateQueries('bookings')
      toast({
        title: 'Refund processed',
        description: `TZS ${refundAmount.toLocaleString()} has been credited back to the commuter wallet.`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      })
      refundModal.onClose()
    } catch (error: any) {
      toast({
        title: 'Refund failed',
        description: error?.response?.data?.error?.message || 'Could not process the refund.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    }
  }

  const handleStatusUpdate = async (bookingId: string, status: string) => {
    try {
      await cmsService.updateBookingStatus(bookingId, status)
      await queryClient.invalidateQueries('bookings')
      toast({
        title: 'Status updated',
        description: 'Booking status has been updated successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
    } catch (error: any) {
      toast({
        title: 'Update failed',
        description: error?.response?.data?.error?.message || 'Failed to update booking status.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      })
    }
  }

  const handleSettlementUpdate = async (booking: Booking, settlementStatus: 'pending' | 'cleared') => {
    try {
      await updateBookingMutation.mutateAsync({
        bookingId: booking.id,
        payload: {
          settlementStatus,
          settlementClearedAt: settlementStatus === 'cleared' ? new Date().toISOString() : '',
          settlementClearedById: settlementStatus === 'cleared' ? 'current-admin' : '',
          paymentResponsibility: booking.adminMetadata?.paymentResponsibility || 'passenger',
          paymentSourceAccount: booking.adminMetadata?.paymentSourceAccount || '',
          passengerName: booking.passenger?.isGuest ? booking.passenger?.name : undefined,
          passengerPhone: booking.passenger?.isGuest ? booking.passenger?.phone : undefined,
          invoiceGroup: booking.adminMetadata?.invoiceGroup || '',
          internalNote: booking.adminMetadata?.internalNote || '',
        },
      })
      await queryClient.invalidateQueries('bookings')
      toast({
        title: settlementStatus === 'cleared' ? 'Marked as cleared' : 'Moved back to pending',
        description: settlementStatus === 'cleared'
          ? 'This booking has been cleared from the invoice queue.'
          : 'This booking will appear in pending settlement again.',
        status: 'success',
        duration: 3500,
        isClosable: true,
      })
    } catch (error: any) {
      toast({
        title: 'Settlement update failed',
        description: error?.response?.data?.error?.message || 'Could not update settlement status.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      })
    }
  }

  if (bookingsLoading) {
    return (
      <Center h="64">
        <Spinner size="lg" color="brand.500" />
      </Center>
    )
  }

  return (
    <VStack spacing={6} align="stretch">
      <Flex align="center" justify="space-between" flexDirection={{ base: 'column', md: 'row' }} gap={4}>
        <VStack align={{ base: 'center', md: 'start' }} spacing={1}>
          <Heading size="xl" color="gray.900">Bookings Management</Heading>
          <Text color="gray.600">Admin can create bookings, reconcile them by account, and clear invoiced trips.</Text>
        </VStack>
        <Button leftIcon={<Plus size={16} />} colorScheme="brand" onClick={manualBookingModal.onOpen}>
          Manual Booking
        </Button>
      </Flex>

      <SimpleGrid columns={{ base: 2, md: 6 }} spacing={4}>
        {stats.map((stat) => (
          <Card key={stat.title} bg="white" shadow="sm">
            <CardBody>
              <HStack justify="space-between">
                <VStack align="start" spacing={1}>
                  <Text fontSize="sm" color="gray.600" fontWeight="medium">{stat.title}</Text>
                  <Text fontSize="xl" fontWeight="bold" color="gray.900">{stat.value}</Text>
                </VStack>
                <Box p={2} borderRadius="lg" bg={`${stat.color}.100`} color={`${stat.color}.600`}>
                  <stat.icon size={20} />
                </Box>
              </HStack>
            </CardBody>
          </Card>
        ))}
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
        <Card bg="white" shadow="sm">
          <CardBody>
            <Text fontSize="sm" color="gray.600">Admin Booking Invoice Total</Text>
            <Text fontSize="2xl" fontWeight="bold" color="gray.900">TZS {invoiceTotal.toLocaleString()}</Text>
            <Text fontSize="xs" color="gray.500">{invoiceBookings.length} manual/admin booking(s) in current discussion set</Text>
          </CardBody>
        </Card>
        <Card bg="white" shadow="sm">
          <CardBody>
            <Text fontSize="sm" color="gray.600">Outstanding Admin Invoice</Text>
            <Text fontSize="2xl" fontWeight="bold" color="orange.500">TZS {outstandingTotal.toLocaleString()}</Text>
            <Text fontSize="xs" color="gray.500">Only manual/admin bookings still waiting to be cleared</Text>
          </CardBody>
        </Card>
        <Card bg="white" shadow="sm">
          <CardBody>
            <Text fontSize="sm" color="gray.600">Cleared Admin Invoice</Text>
            <Text fontSize="2xl" fontWeight="bold" color="green.500">TZS {clearedTotal.toLocaleString()}</Text>
            <Text fontSize="xs" color="gray.500">Manual/admin bookings already settled by admin or account</Text>
          </CardBody>
        </Card>
      </SimpleGrid>

      <Card bg="white" shadow="sm">
        <CardBody>
          <VStack spacing={4} align="stretch">
            <HStack spacing={4} flexDirection={{ base: 'column', md: 'row' }} align={{ base: 'stretch', md: 'center' }}>
              <InputGroup maxW="420px">
                <InputLeftElement pointerEvents="none">
                  <Search size={16} color="var(--chakra-colors-gray-400)" />
                </InputLeftElement>
                <Input
                  placeholder="Search by booking, passenger, route, or account..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
              <Input
                maxW="240px"
                placeholder="Filter by account name"
                value={accountFilter}
                onChange={(e) => setAccountFilter(e.target.value)}
              />
            </HStack>

            <SimpleGrid columns={{ base: 2, md: 5 }} spacing={3}>
              <Select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
                <option value="all">All Status</option>
                <option value="confirmed">Confirmed</option>
                <option value="pending">Pending</option>
                <option value="cancelled">Cancelled</option>
                <option value="unused">Unused Tickets</option>
              </Select>

              <Select value={selectedPaymentStatus} onChange={(e) => setSelectedPaymentStatus(e.target.value)}>
                <option value="all">All Payments</option>
                <option value="paid">Paid</option>
                <option value="pending">Pending</option>
                <option value="refunded">Refunded</option>
                <option value="failed">Failed</option>
              </Select>

              <Select value={selectedSettlementStatus} onChange={(e) => setSelectedSettlementStatus(e.target.value)}>
                <option value="all">All Settlement</option>
                <option value="pending">Pending</option>
                <option value="cleared">Cleared</option>
              </Select>

              <Select value={selectedResponsibility} onChange={(e) => setSelectedResponsibility(e.target.value)}>
                <option value="all">All Responsibility</option>
                <option value="passenger">Passenger</option>
                <option value="admin">Admin</option>
                <option value="account">Account</option>
              </Select>

              <Button
                variant="outline"
                onClick={() => {
                  setSearchTerm('')
                  setSelectedStatus('all')
                  setSelectedPaymentStatus('all')
                  setSelectedSettlementStatus('all')
                  setSelectedResponsibility('all')
                  setAccountFilter('')
                }}
              >
                Reset Filters
              </Button>
            </SimpleGrid>
          </VStack>
        </CardBody>
      </Card>

      <Card bg="white" shadow="sm">
        <CardHeader>
          <Flex justify="space-between" align="center">
            <Heading size="md" color="gray.900">Bookings List</Heading>
            <Text color="gray.600" fontSize="sm">{filteredBookings.length} booking(s)</Text>
          </Flex>
        </CardHeader>
        <CardBody>
          <Box overflowX="auto">
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Booking</Th>
                  <Th>Passenger</Th>
                  <Th>Route</Th>
                  <Th>Trip</Th>
                  <Th>Amount</Th>
                  <Th>Billing</Th>
                  <Th>Settlement</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {paginatedBookings.length === 0 ? (
                  <Tr>
                    <Td colSpan={8} py={12}>
                      <Center>
                        <VStack spacing={3}>
                          <Calendar size={42} color="var(--chakra-colors-gray-400)" />
                          <Heading size="sm">No bookings found</Heading>
                          <Text color="gray.600" textAlign="center">
                            Nothing matches the current filter set.
                          </Text>
                        </VStack>
                      </Center>
                    </Td>
                  </Tr>
                ) : (
                  paginatedBookings.map((booking) => {
                    const paymentStatus = booking.payment?.status || 'pending'
                    const settlementStatus = booking.adminMetadata?.settlementStatus || 'pending'
                    const responsibility = booking.adminMetadata?.paymentResponsibility || 'passenger'
                    const accountName = booking.adminMetadata?.paymentSourceAccount

                    return (
                      <Tr key={booking.id} _hover={{ bg: 'gray.50' }}>
                        <Td>
                          <VStack align="start" spacing={1}>
                            <Text fontWeight="semibold" color="blue.600" cursor="pointer" onClick={() => handleViewDetails(booking)}>
                              {booking.bookingReference}
                            </Text>
                            <HStack spacing={2}>
                              <Badge colorScheme={booking.adminMetadata?.bookingSource === 'admin' ? 'purple' : 'gray'}>
                                {booking.adminMetadata?.bookingSource === 'admin' ? 'Admin' : 'Passenger'}
                              </Badge>
                              <Badge colorScheme={getStatusColor(booking.status)} textTransform="capitalize">
                                {booking.status}
                              </Badge>
                            </HStack>
                          </VStack>
                        </Td>

                        <Td>
                          <VStack align="start" spacing={1}>
                            <Text fontWeight="medium">{booking.passenger?.name || 'Unknown passenger'}</Text>
                            <Text fontSize="xs" color="gray.600">{booking.passenger?.phone || '—'}</Text>
                            {booking.passenger?.isGuest ? <Badge colorScheme="purple">Traveller Override</Badge> : null}
                          </VStack>
                        </Td>

                        <Td>
                          <VStack align="start" spacing={1}>
                            <Text fontWeight="medium">{booking.route?.name || 'Unknown route'}</Text>
                            <Text fontSize="xs" color="gray.600">{booking.route?.startPoint} → {booking.route?.endPoint}</Text>
                          </VStack>
                        </Td>

                        <Td>
                          <VStack align="start" spacing={1}>
                            <Text fontSize="sm">{formatTripDateTime(booking.trip?.scheduledDeparture || booking.createdAt)}</Text>
                            <Text fontSize="xs" color="gray.600">Seats: {booking.seatNumbers?.join(', ') || booking.seatCount || '—'}</Text>
                          </VStack>
                        </Td>

                        <Td>
                          <VStack align="start" spacing={1}>
                            <Text fontWeight="semibold">TZS {Number(booking.totalFare || 0).toLocaleString()}</Text>
                            <Badge colorScheme={getPaymentStatusColor(paymentStatus)} textTransform="capitalize">
                              {paymentStatus}
                            </Badge>
                          </VStack>
                        </Td>

                        <Td>
                          <VStack align="start" spacing={1}>
                            <Badge colorScheme={getResponsibilityColor(responsibility)} textTransform="capitalize">
                              {responsibility}
                            </Badge>
                            <Text fontSize="xs" color="gray.600">{accountName || 'No account linked'}</Text>
                          </VStack>
                        </Td>

                        <Td>
                          <VStack align="start" spacing={1}>
                            <Badge colorScheme={settlementStatus === 'cleared' ? 'green' : 'orange'} textTransform="capitalize">
                              {settlementStatus}
                            </Badge>
                            <Text fontSize="xs" color="gray.600">
                              {booking.adminMetadata?.settlementClearedAt
                                ? `Cleared ${new Date(booking.adminMetadata.settlementClearedAt).toLocaleDateString()}`
                                : 'Awaiting end-of-day clearance'}
                            </Text>
                          </VStack>
                        </Td>

                        <Td>
                          <HStack spacing={2}>
                            <Tooltip label="View details">
                              <IconButton
                                aria-label="View booking details"
                                icon={<Eye size={16} />}
                                size="sm"
                                variant="ghost"
                                onClick={() => handleViewDetails(booking)}
                              />
                            </Tooltip>

                            {isUnusedTicket(booking) ? (
                              <Tooltip label="Void ticket and refund">
                                <IconButton
                                  aria-label="Refund booking"
                                  icon={<RefreshCcw size={16} />}
                                  size="sm"
                                  colorScheme="orange"
                                  onClick={() => handleOpenRefund(booking)}
                                />
                              </Tooltip>
                            ) : null}

                            <Tooltip label={settlementStatus === 'cleared' ? 'Move back to pending' : 'Mark as cleared'}>
                              <IconButton
                                aria-label="Toggle settlement"
                                icon={settlementStatus === 'cleared' ? <RefreshCcw size={16} /> : <CheckCircle size={16} />}
                                size="sm"
                                colorScheme={settlementStatus === 'cleared' ? 'gray' : 'green'}
                                variant={settlementStatus === 'cleared' ? 'outline' : 'solid'}
                                isLoading={updateBookingMutation.isLoading}
                                onClick={() => handleSettlementUpdate(booking, settlementStatus === 'cleared' ? 'pending' : 'cleared')}
                              />
                            </Tooltip>

                            <Select
                              size="sm"
                              w="120px"
                              value={booking.status}
                              onChange={(e) => handleStatusUpdate(booking.id, e.target.value)}
                            >
                              <option value="pending">Pending</option>
                              <option value="confirmed">Confirmed</option>
                              <option value="cancelled">Cancelled</option>
                            </Select>
                          </HStack>
                        </Td>
                      </Tr>
                    )
                  })
                )}
              </Tbody>
            </Table>
          </Box>

          {totalPages > 1 ? (
            <HStack justify="center" py={4} borderTop="1px solid" borderColor="gray.100" mt={4}>
              <Button size="sm" variant="outline" onClick={() => setCurrentPage((page) => Math.max(page - 1, 1))} isDisabled={currentPage === 1}>
                Previous
              </Button>
              <Text fontSize="sm" color="gray.600">Page {currentPage} of {totalPages}</Text>
              <Button size="sm" variant="outline" onClick={() => setCurrentPage((page) => Math.min(page + 1, totalPages))} isDisabled={currentPage === totalPages}>
                Next
              </Button>
            </HStack>
          ) : null}
        </CardBody>
      </Card>

      <ManualBookingModal isOpen={manualBookingModal.isOpen} onClose={manualBookingModal.onClose} />

      <Modal isOpen={detailModal.isOpen} onClose={detailModal.onClose} size="xl">
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent borderRadius="xl">
          <ModalHeader borderBottom="1px solid" borderColor="gray.100">
            <VStack align="start" spacing={0}>
              <Text fontSize="lg" fontWeight="bold">Booking Details</Text>
              <Text fontSize="sm" color="gray.500">{selectedBooking?.bookingReference}</Text>
            </VStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={6}>
            {selectedBooking ? (
              <VStack spacing={5} align="stretch">
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <Box>
                    <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="bold" mb={2}>Passenger</Text>
                    <VStack align="start" spacing={1}>
                      <Text fontWeight="bold">{selectedBooking.passenger?.name || '—'}</Text>
                      <HStack spacing={2}><Phone size={12} /><Text fontSize="sm">{selectedBooking.passenger?.phone || '—'}</Text></HStack>
                      <Text fontSize="sm" color="gray.600">{selectedBooking.passenger?.email || '—'}</Text>
                    </VStack>
                  </Box>

                  <Box>
                    <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="bold" mb={2}>Payment & Settlement</Text>
                    <VStack align="start" spacing={2}>
                      <Badge colorScheme={getPaymentStatusColor(selectedBooking.payment?.status || 'pending')} textTransform="capitalize">
                        Payment: {selectedBooking.payment?.status || 'pending'}
                      </Badge>
                      <Badge colorScheme={getResponsibilityColor(selectedBooking.adminMetadata?.paymentResponsibility || 'passenger')} textTransform="capitalize">
                        Billed to: {selectedBooking.adminMetadata?.paymentResponsibility || 'passenger'}
                      </Badge>
                      <Badge colorScheme={(selectedBooking.adminMetadata?.settlementStatus || 'pending') === 'cleared' ? 'green' : 'orange'} textTransform="capitalize">
                        Settlement: {selectedBooking.adminMetadata?.settlementStatus || 'pending'}
                      </Badge>
                      <Text fontSize="sm" fontWeight="bold">TZS {selectedBooking.totalFare.toLocaleString()}</Text>
                    </VStack>
                  </Box>
                </SimpleGrid>

                <Box bg="gray.50" p={4} borderRadius="lg">
                  <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="bold" mb={3}>Trip Information</Text>
                  <VStack align="stretch" spacing={3}>
                    <Box>
                      <Text fontWeight="bold">{selectedBooking.route?.name || 'Route unavailable'}</Text>
                      <Text fontSize="sm" color="gray.600">{selectedBooking.route?.startPoint} → {selectedBooking.route?.endPoint}</Text>
                    </Box>

                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                      <Box>
                        <HStack spacing={2} mb={1}>
                          <Clock size={14} color="var(--chakra-colors-blue-600)" />
                          <Text fontSize="xs" color="gray.600" fontWeight="semibold">Scheduled departure</Text>
                        </HStack>
                        <Text fontWeight="bold" fontSize="sm">{formatTripDateTime(selectedBooking.trip?.scheduledDeparture)}</Text>
                      </Box>
                      <Box>
                        <HStack spacing={2} mb={1}>
                          <Clock size={14} color="var(--chakra-colors-purple-600)" />
                          <Text fontSize="xs" color="gray.600" fontWeight="semibold">Scheduled arrival</Text>
                        </HStack>
                        <Text fontWeight="bold" fontSize="sm">{formatTripDateTime(selectedBooking.trip?.scheduledArrival)}</Text>
                      </Box>
                    </SimpleGrid>

                    {selectedBooking.trip?.timetable ? (
                      <Text fontSize="xs" color="gray.500">
                        Timetable slot: {TRIP_DAY_NAMES[selectedBooking.trip.timetable.dayOfWeek] ?? `Day ${selectedBooking.trip.timetable.dayOfWeek}`} · {formatTripTimeOnly(selectedBooking.trip.timetable.departureTime ?? null)}
                      </Text>
                    ) : null}

                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                      <Box>
                        <HStack spacing={2} mb={1}>
                          <MapPin size={14} color="var(--chakra-colors-green-600)" />
                          <Text fontSize="xs" color="gray.600" fontWeight="semibold">Boarding stop</Text>
                        </HStack>
                        <Text fontWeight="bold" fontSize="sm">{formatStopLabel(selectedBooking.boardingStop)}</Text>
                        <Text fontSize="xs" color="gray.500">{selectedBooking.boardingStop?.estimatedPickupAt ? formatTripDateTime(selectedBooking.boardingStop.estimatedPickupAt) : 'No pickup estimate'}</Text>
                      </Box>
                      <Box>
                        <HStack spacing={2} mb={1}>
                          <MapPin size={14} color="var(--chakra-colors-red-500)" />
                          <Text fontSize="xs" color="gray.600" fontWeight="semibold">Drop-off stop</Text>
                        </HStack>
                        <Text fontWeight="bold" fontSize="sm">{formatStopLabel(selectedBooking.alightingStop)}</Text>
                      </Box>
                    </SimpleGrid>
                  </VStack>
                </Box>

                <Box bg="gray.50" p={4} borderRadius="lg">
                  <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="bold" mb={3}>Admin Metadata</Text>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <Box>
                      <Text fontSize="xs" color="gray.600">Source</Text>
                      <Text fontWeight="semibold" textTransform="capitalize">{selectedBooking.adminMetadata?.bookingSource || 'passenger'}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.600">Account</Text>
                      <Text fontWeight="semibold">{selectedBooking.adminMetadata?.paymentSourceAccount || '—'}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.600">Invoice Group</Text>
                      <Text fontWeight="semibold">{selectedBooking.adminMetadata?.invoiceGroup || '—'}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.600">Cleared At</Text>
                      <Text fontWeight="semibold">{selectedBooking.adminMetadata?.settlementClearedAt ? formatTripDateTime(selectedBooking.adminMetadata.settlementClearedAt) : '—'}</Text>
                    </Box>
                  </SimpleGrid>
                  {selectedBooking.adminMetadata?.internalNote ? (
                    <>
                      <Divider my={3} />
                      <Text fontSize="xs" color="gray.600" mb={1}>Internal Note</Text>
                      <Text fontSize="sm">{selectedBooking.adminMetadata.internalNote}</Text>
                    </>
                  ) : null}
                </Box>

                <Text fontSize="xs" fontFamily="mono" color="gray.400">{selectedBooking.id}</Text>
              </VStack>
            ) : null}
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal isOpen={refundModal.isOpen} onClose={refundModal.onClose} size="md">
        <ModalOverlay backdropFilter="blur(8px)" bg="blackAlpha.600" />
        <ModalContent borderRadius="2xl">
          <ModalHeader>Void Ticket & Refund</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={5} align="stretch">
              <Alert status="warning" borderRadius="xl">
                <AlertIcon />
                <AlertDescription fontSize="sm">
                  This will cancel the booking and credit the refund to the commuter wallet.
                </AlertDescription>
              </Alert>

              <Card bg="gray.50" border="1px solid" borderColor="gray.200">
                <CardBody>
                  <SimpleGrid columns={2} spacing={3}>
                    <Box>
                      <Text fontSize="xs" color="gray.500">Commuter</Text>
                      <Text fontSize="sm" fontWeight="semibold">{selectedBooking?.passenger?.name || '—'}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.500">Route</Text>
                      <Text fontSize="sm" fontWeight="semibold">{selectedBooking?.route?.name || '—'}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.500">Status</Text>
                      <Badge colorScheme="green">{selectedBooking?.status}</Badge>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.500">Boarded</Text>
                      <Badge colorScheme={selectedBooking?.boardedAt ? 'green' : 'red'}>
                        {selectedBooking?.boardedAt ? 'Yes' : 'No'}
                      </Badge>
                    </Box>
                  </SimpleGrid>
                </CardBody>
              </Card>

              <FormControl>
                <FormLabel>Refund Amount (TZS)</FormLabel>
                <NumberInput
                  min={0}
                  max={selectedBooking?.totalFare ?? 0}
                  value={refundAmount}
                  onChange={(_, valueAsNumber) => setRefundAmount(Number.isNaN(valueAsNumber) ? 0 : valueAsNumber)}
                >
                  <NumberInputField />
                </NumberInput>
              </FormControl>

              <FormControl>
                <FormLabel>Reason</FormLabel>
                <Textarea
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  placeholder="Reason for voiding this ticket"
                  rows={3}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button variant="ghost" onClick={refundModal.onClose}>Cancel</Button>
              <Button colorScheme="orange" onClick={handleConfirmRefund} isLoading={processRefundMutation.isLoading}>
                Void & Refund
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  )
}
